import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import styled, { keyframes } from 'styled-components'
import { FixedSizeList as List } from 'react-window'
import { 
  Bell, 
  ListTodo, 
  Inbox, 
  RotateCcw, 
  ChevronDown,
  Building2,
  Rocket,
  Award,
  Filter,
  Sparkles
} from 'lucide-react'
import { HistoryItem } from './components/LogItem'
import { semanticColors } from '@/common/styles/colorTokens'
import { useNavigationStore } from '@/common/store'
import { typography } from '@/common/styles/typographyTokens'
import { notificationService } from '@/services/notificationService'
import { Notification } from '@/common/types/notification'

interface ServiceIdOption {
  id: number
  name: string
  icon: React.ReactNode
}

const serviceIdOptions: ServiceIdOption[] = [
  { id: 0, name: '전체 서비스', icon: <Filter size={16} /> },
  { id: 1, name: '스타트업', icon: <Rocket size={16} /> },
  { id: 2, name: '투자사', icon: <Building2 size={16} /> },
  { id: 3, name: '선발 프로그램', icon: <Award size={16} /> }
]

const typeOptions = [
  { value: '전체', label: '전체', icon: <Bell size={16} /> },
  { value: '할 일', label: '할 일', icon: <ListTodo size={16} /> },
  { value: '알림', label: '알림', icon: <Bell size={16} /> }
]

// 아이템 높이 (padding 포함)
const ITEM_HEIGHT = 140

const NotificationPage: React.FC = () => {
  const { setHeaderTitle,   setBackNavigation } = useNavigationStore()
  const [selectedType, setSelectedType] = useState('전체')
  const [selectedServiceId, setSelectedServiceId] = useState<ServiceIdOption>(serviceIdOptions[0])
  const [typeOpen, setTypeOpen] = useState(false)
  const [serviceOpen, setServiceOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [listDimensions, setListDimensions] = useState({ width: 0, height: 0 })
  const ITEMS_PER_PAGE = 20
  const typeRef = useRef<HTMLDivElement>(null)
  const serviceRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<List>(null)
  const listContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeRef.current && !typeRef.current.contains(event.target as Node)) {
        setTypeOpen(false)
      }
      if (serviceRef.current && !serviceRef.current.contains(event.target as Node)) {
        setServiceOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 리스트 컨테이너 크기 관찰
  useEffect(() => {
    const container = listContainerRef.current
    if (!container) return

    // 초기 크기 설정
    const { width, height } = container.getBoundingClientRect()
    setListDimensions({ width, height })

    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      setListDimensions({ width, height })
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [isInitialLoading, notifications.length])

  const handleTypeChange = (type: string) => {
    setSelectedType(type)
    setTypeOpen(false)
    setPage(1)
    setHasMore(true)
    setNotifications([])
    const serviceId = selectedServiceId.id === 0 ? undefined : selectedServiceId.id
    const newType = type === '할 일' ? 'TASK' : type === '알림' ? 'NOTIFICATION' : undefined
    fetchNotifications(1, ITEMS_PER_PAGE, newType, serviceId)
  }

  const handleServiceIdChange = (serviceId: ServiceIdOption) => {
    setSelectedServiceId(serviceId)
    setServiceOpen(false)
    setPage(1)
    setHasMore(true)
    setNotifications([])
    const type = selectedType === '할 일' ? 'TASK' : selectedType === '알림' ? 'NOTIFICATION' : undefined
    const newServiceId = serviceId.id === 0 ? undefined : serviceId.id
    fetchNotifications(1, ITEMS_PER_PAGE, type, newServiceId)
  }

  const fetchNotifications = async (
    currentPage: number,
    limit: number,
    type?: 'TASK' | 'NOTIFICATION',
    serviceId?: number
  ) => {
    try {
      setIsLoading(true)
      const response = await notificationService.getNotifications(currentPage, limit, type, serviceId)
      const notiArray = response.data
      const { totalItems, totalPages } = response.meta

      if (currentPage === 1) {
        setNotifications(notiArray)
      } else {
        setNotifications(prev => [...prev, ...notiArray])
      }
      setHasMore(currentPage < totalPages)
      setTotalItems(totalItems)
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      setHasMore(false)
    } finally {
      setIsLoading(false)
      setIsInitialLoading(false)
    }
  }

  // 무한 스크롤 - 스크롤 끝에 도달하면 더 불러오기
  const loadMoreItems = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      const type = selectedType === '할 일' ? 'TASK' : selectedType === '알림' ? 'NOTIFICATION' : undefined
      const serviceId = selectedServiceId.id === 0 ? undefined : selectedServiceId.id
      fetchNotifications(nextPage, ITEMS_PER_PAGE, type, serviceId)
    }
  }, [isLoading, hasMore, page, selectedType, selectedServiceId])

  // 스크롤 이벤트 핸들러
  const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested }: { scrollOffset: number; scrollUpdateWasRequested: boolean }) => {
    if (scrollUpdateWasRequested) return
    
    const listHeight = notifications.length * ITEM_HEIGHT
    const threshold = 300 // 끝에서 300px 전에 로드
    
    if (listHeight - scrollOffset < window.innerHeight + threshold) {
      loadMoreItems()
    }
  }, [notifications.length, loadMoreItems])

  useEffect(() => {
    setHeaderTitle('알림 센터');    
    setBackNavigation('마이스페이스', '/my-space')
    fetchNotifications(1, ITEMS_PER_PAGE)
  }, [])

  const resetFilters = () => {
    setSelectedType('전체')
    setSelectedServiceId(serviceIdOptions[0])
    setPage(1)
    setHasMore(true)
    setNotifications([])
    fetchNotifications(1, ITEMS_PER_PAGE)
  }

  const hasActiveFilter = selectedType !== '전체' || selectedServiceId.id !== 0

  // 가상 스크롤 Row 컴포넌트
  const Row = useCallback(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const notification = notifications[index]
    
    // 로딩 표시 (마지막 아이템 다음)
    if (!notification) {
      return (
        <div style={style}>
          <LoadingMore>
            <LoadingSpinnerSmall />
            <span>더 불러오는 중...</span>
          </LoadingMore>
        </div>
      )
    }

    return (
      <div style={{ ...style, paddingBottom: '12px' }}>
        <HistoryItem
          notification={notification}
          onClick={() => {
            if (notification.url) {
              const url = notification.url + '?n_id=' + notification.id
              window.open(url, '_blank')
            }
          }}
        />
      </div>
    )
  }, [notifications])

  // 아이템 개수 (로딩 중이면 +1)
  const itemCount = hasMore ? notifications.length + 1 : notifications.length

  return (
    <PageContainer>
      <ContentWrapper>
        {/* 히어로 섹션 */}
        <HeroSection>
          <HeroBackground>
            <HeroGradient />
            <HeroPattern />
          </HeroBackground>
          <HeroContent>
            <HeroIconWrapper>
              <Bell size={32} />
              <HeroIconBadge>
                <Sparkles size={12} />
              </HeroIconBadge>
            </HeroIconWrapper>
            <HeroTextContent>
              <HeroTitle>알림 센터</HeroTitle>
              <HeroDescription>
                새로운 소식과 할 일을 확인하세요
              </HeroDescription>
            </HeroTextContent>
            <HeroStats>
              <HeroStatItem>
                <HeroStatIcon $type="total">
                  <Bell size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{totalItems}</HeroStatValue>
                  <HeroStatLabel>전체</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
            </HeroStats>
          </HeroContent>
        </HeroSection>

        {/* 메인 카드 */}
        <MainCard>
          <CardHeader>
            <CardTitleWrapper>
              <CardIconWrapper>
                <Bell size={20} />
              </CardIconWrapper>
              <div>
                <CardTitle>알림 목록</CardTitle>
                <CardSubtitle>
                  {hasActiveFilter 
                    ? `필터링된 결과 ${totalItems}개` 
                    : `총 ${totalItems}개의 알림`}
                </CardSubtitle>
              </div>
            </CardTitleWrapper>

            {/* 필터 영역 */}
            <FilterWrapper>
              <FilterDropdown ref={typeRef}>
                <FilterButton 
                  onClick={() => setTypeOpen(!typeOpen)} 
                  $isActive={selectedType !== '전체'}
                  $isOpen={typeOpen}
                >
                  {typeOptions.find(o => o.value === selectedType)?.icon}
                  <span>{selectedType}</span>
                  <ChevronDown size={14} />
                </FilterButton>
                {typeOpen && (
                  <Dropdown>
                    {typeOptions.map(option => (
                      <DropdownItem
                        key={option.value}
                        onClick={() => handleTypeChange(option.value)}
                        $isSelected={selectedType === option.value}
                      >
                        {option.icon}
                        <span>{option.label}</span>
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </FilterDropdown>

              <FilterDropdown ref={serviceRef}>
                <FilterButton 
                  onClick={() => setServiceOpen(!serviceOpen)} 
                  $isActive={selectedServiceId.id !== 0}
                  $isOpen={serviceOpen}
                >
                  {selectedServiceId.icon}
                  <span>{selectedServiceId.name}</span>
                  <ChevronDown size={14} />
                </FilterButton>
                {serviceOpen && (
                  <Dropdown>
                    {serviceIdOptions.map(option => (
                      <DropdownItem
                        key={option.id}
                        onClick={() => handleServiceIdChange(option)}
                        $isSelected={selectedServiceId.id === option.id}
                      >
                        {option.icon}
                        <span>{option.name}</span>
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </FilterDropdown>

              {hasActiveFilter && (
                <ResetButton onClick={resetFilters}>
                  <RotateCcw size={14} />
                  <span>초기화</span>
                </ResetButton>
              )}
            </FilterWrapper>
          </CardHeader>

          <CardContent>
            {isInitialLoading ? (
              <LoadingContainer>
                <LoadingSpinner />
                <LoadingText>알림을 불러오는 중...</LoadingText>
              </LoadingContainer>
            ) : notifications.length === 0 ? (
              <EmptyState>
                <EmptyIconWrapper>
                  <Inbox size={48} />
                </EmptyIconWrapper>
                <EmptyTitle>
                  {hasActiveFilter ? '조건에 맞는 알림이 없습니다' : '알림이 없습니다'}
                </EmptyTitle>
                <EmptyDescription>
                  {hasActiveFilter 
                    ? '다른 필터를 선택하거나 초기화해보세요' 
                    : '새로운 알림이 도착하면 여기에 표시됩니다'}
                </EmptyDescription>
                {hasActiveFilter && (
                  <EmptyResetButton onClick={resetFilters}>
                    <RotateCcw size={16} />
                    필터 초기화
                  </EmptyResetButton>
                )}
              </EmptyState>
            ) : (
              <VirtualListWrapper ref={listContainerRef}>
                {listDimensions.height > 0 && (
                  <List
                    ref={listRef}
                    height={listDimensions.height}
                    width={listDimensions.width}
                    itemCount={itemCount}
                    itemSize={ITEM_HEIGHT}
                    onScroll={handleScroll}
                    overscanCount={5}
                  >
                    {Row}
                  </List>
                )}
                {!hasMore && notifications.length > 0 && (
                  <EndMessage>
                    모든 알림을 확인했습니다 ✓
                  </EndMessage>
                )}
              </VirtualListWrapper>
            )}
          </CardContent>
        </MainCard>
      </ContentWrapper>
    </PageContainer>
  )
}

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`

// Styled Components
const PageContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: ${semanticColors.background.primary};
  overflow: hidden;
`

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`

// 히어로 섹션
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
  border-radius: 24px;
  overflow: hidden;
  flex-shrink: 0;
  animation: ${fadeInUp} 0.6s ease-out;
`

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 50%, #ec4899 100%);
`

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 40%);
`

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`

const HeroIconWrapper = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  color: white;
  flex-shrink: 0;
`

const HeroIconBadge = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border: 3px solid #f59e0b;
  border-radius: 50%;
  color: #fff;
`

const HeroTextContent = styled.div`
  flex: 1;
`

const HeroTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.375rem;
`

const HeroDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.9);
`

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.875rem 1.25rem;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 14px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

const HeroStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`

const HeroStatIcon = styled.div<{ $type: 'total' | 'task' }>`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #fff;
  background: ${props => props.$type === 'total' 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(59, 130, 246, 0.3)'};
`

const HeroStatInfo = styled.div`
  display: flex;
  flex-direction: column;
`

const HeroStatValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
`

const HeroStatLabel = styled.span`
  ${typography.caption.caption2}
  color: rgba(255,255,255,0.85);
`

const HeroStatDivider = styled.div`
  width: 1px;
  height: 36px;
  background: rgba(255,255,255,0.2);
`

// 메인 카드
const MainCard = styled.div`
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
  gap: 1rem;
  flex-wrap: wrap;
  flex-shrink: 0;
`

const CardTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
`

const CardIconWrapper = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, ${semanticColors.background.warning.secondary} 0%, ${semanticColors.background.warning.secondaryHover} 100%);
  color: ${semanticColors.text.onWarning.primary};
`

const CardTitle = styled.h2`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`

const CardSubtitle = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-top: 0.125rem;
`

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const FilterDropdown = styled.div`
  position: relative;
`

const FilterButton = styled.button<{ $isActive: boolean; $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid ${props => props.$isActive 
    ? semanticColors.background.tertiary 
    : semanticColors.border.primary};
  border-radius: 10px;
  background: ${props => props.$isActive 
    ? semanticColors.background.brandHover 
    : semanticColors.background.secondary};
  color: ${props => props.$isActive 
    ? 'white' 
    : semanticColors.text.primary};
  ${typography.caption.caption1.medium}
  cursor: pointer;
  transition: all 0.2s ease;

  svg:last-child {
    transition: transform 0.2s ease;
    transform: rotate(${props => props.$isOpen ? '180deg' : '0deg'});
    opacity: 0.6;
  }

  &:hover {
    border-color: ${semanticColors.background.tertiary};
    ${props => !props.$isActive && `
      background: ${semanticColors.background.tertiary}10;
    `}
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 160px;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 100;
  overflow: hidden;
  animation: ${fadeInUp} 0.15s ease-out;
`

const DropdownItem = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: ${props => props.$isSelected 
    ? semanticColors.background.brandHover 
    : 'transparent'};
  color: ${props => props.$isSelected 
    ? 'white' 
    : semanticColors.text.primary};
  ${typography.caption.caption1.default}
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.$isSelected 
      ? semanticColors.background.brandHover 
      : semanticColors.background.secondaryHover};
  }
`

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  background: ${semanticColors.background.success.primary};
  color: ${semanticColors.text.white};
  ${typography.caption.caption2}
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${semanticColors.background.success.primaryHover};
  }
`

const CardContent = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const VirtualListWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;
  height: 100%;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 1rem;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${semanticColors.border.primary};
  border-top-color: ${semanticColors.background.brand};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

const LoadingText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  animation: ${pulse} 1.5s ease-in-out infinite;
`

const LoadingMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 1.5rem;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`

const LoadingSpinnerSmall = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${semanticColors.border.primary};
  border-top-color: ${semanticColors.background.brand};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const EndMessage = styled.div`
  text-align: center;
  padding: 1rem;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  border-top: 1px dashed ${semanticColors.border.primary};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${semanticColors.background.secondary};
`

// 빈 상태
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  padding: 2rem;
`

const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.primary};
  border: 2px dashed ${semanticColors.border.primary};
  border-radius: 20px;
  color: ${semanticColors.text.disabled};
  margin-bottom: 1.5rem;
`

const EmptyTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`

const EmptyDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 1.5rem;
`

const EmptyResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: ${semanticColors.background.tertiary};
  border: none;
  border-radius: 10px;
  ${typography.caption.caption1.medium}
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${semanticColors.background.tertiaryHover};
    transform: translateY(-2px);
  }
`

export default NotificationPage
